import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import Icon from '@src/basic-components/Icon/Icon';
import { getPostComment } from '@src/model/upvote/helpers';
import type { CommentCollectionType } from '@src/store/comments/comments';
import { useComment } from '@src/store/comments/hooks/useComment';
import { usePaginatedComments } from '@src/store/comments/hooks/usePaginatedComments';
import { CommentStatus } from '@src/store/contentEntities/model';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface LoadMoreCommentsButtonProps {
    className?: string;
    tokenName: string;
    userId?: string; // required if type is 'user'
    postId?: string; // required if type is 'post'
    parentCommentId?: string; // required if type is 'comment'
    includeDeleted?: boolean;
    type: CommentCollectionType;
}

export function LoadMoreCommentsButton(props: LoadMoreCommentsButtonProps) {
    const { className = '', tokenName, type, includeDeleted, parentCommentId = '', postId = '', userId = '' } = props;
    const parentComment = useComment(parentCommentId);

    const { loadMore, loadStatus, commentIds } = usePaginatedComments({
        type,
        fetchLimit: 20,
        parentCommentId,
        postId,
        tokenName,
        userId,
        includeDeleted,
        includeMyReactions: true,
    });
    const isLoading = loadStatus === 'loading';
    function getBaseClassName() {
        const base = 'text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-50';
        return cn(base, 'flex items-center space-x-1');
    }

    function getTotalMoreReplies() {
        if (type !== 'comment') return 0;
        if (!parentComment) return 0;
        let total = 0;
        for (const commentId of commentIds) {
            const comment = getPostComment(commentId);
            if (!comment) continue;
            if (comment.status !== CommentStatus.PUBLISHED) continue;
            total++;
        }
        return parentComment.numComments - commentIds.length;
    }

    const { t } = useTranslation();

    return (
        <Button color="base" variant="ghost" size="xs" onClick={loadMore} isLoading={isLoading} className={cn(getBaseClassName(), className)}>
            <Icon className={cn('text-sm', isLoading ? 'animate-spin' : '')} name="refresh" />
            <Text>{t(`commentList.${type}.loadMoreButton`, { count: getTotalMoreReplies() })}</Text>
        </Button>
    );
}
